import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';

const SESSION_KEY = 'agenticpd_tenant';
const DEVICE_KEY = 'agenticpd_device_token';

/** Host labels that are the app/infra itself, never a tenant. */
const NON_TENANT_LABELS = new Set(['localhost', 'www', 'apdcustomer']);

/**
 * Stable per-browser identifier. Persisted in localStorage so it survives tabs
 * and refreshes within the same browser but differs across browsers/devices —
 * this is what locks a PD session to the device that started it (CU-F-011).
 */
function getDeviceToken(): string {
  let token = localStorage.getItem(DEVICE_KEY);
  if (!token) {
    token = crypto.randomUUID();
    localStorage.setItem(DEVICE_KEY, token);
  }
  return token;
}

/**
 * Call once on app boot.
 * Reads ?tenant= from the PD link URL and persists it to sessionStorage so all
 * subsequent HTTP calls include x-tenant-id automatically.
 * Always overwrites stale values when a fresh ?tenant= param is present.
 */
export function bootstrapTenantFromUrl(): void {
  const params = new URLSearchParams(globalThis.location.search);
  const fromUrl = params.get('tenant')?.trim();
  if (fromUrl) {
    sessionStorage.setItem(SESSION_KEY, fromUrl);
    return;
  }
  // If no ?tenant= in URL but we're on a tenant subdomain (e.g. dell.localhost), use that.
  // Skip non-tenant host labels: local dev hostnames and the app's own deployed host
  // (apdcustomer.katdev.com) — its first label is not a tenant.
  const hostname = globalThis.location.hostname;
  const parts = hostname.split('.');
  if (parts.length >= 2 && !NON_TENANT_LABELS.has(parts[0])) {
    sessionStorage.setItem(SESSION_KEY, parts[0]);
    return;
  }

  // Plain localhost with a PD token but no tenant — drop stale tenant from another bank.
  if (params.get('token') && hostname === 'localhost') {
    sessionStorage.removeItem(SESSION_KEY);
  }
}

/** The tenant this browser tab is working for (from ?tenant= or the host subdomain), if known. */
export function currentTenant(): string | null {
  return sessionStorage.getItem(SESSION_KEY);
}

@Injectable()
export class TenantInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const headers: Record<string, string> = { 'x-device-token': getDeviceToken() };
    const tenant = sessionStorage.getItem(SESSION_KEY);
    if (tenant) {
      headers['x-tenant-id'] = tenant;
    }
    return next.handle(req.clone({ setHeaders: headers }));
  }
}
